// Per-pixel field painters. Analytic intensity fields give crisp, perfectly soft
// edges for small FX sprites and let one colour ramp produce "white-hot core,
// coloured edge" projectiles without any blur passes.

import { clamp, type G } from './canvas';

/** Colour ramp stop: intensity 0..1 → colour + alpha. */
export type RampStop = readonly [t: number, color: number, alpha: number];

/** 256-entry RGBA lookup table built from ramp stops (intensity below the first stop is transparent). */
export function buildRamp(stops: readonly RampStop[]): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256 * 4);
  const sorted = [...stops].sort((a, b) => a[0] - b[0]);
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    let r = 0;
    let gg = 0;
    let b = 0;
    let a = 0;
    if (t >= sorted[0][0]) {
      let k = 0;
      while (k < sorted.length - 1 && t > sorted[k + 1][0]) k++;
      const s0 = sorted[k];
      const s1 = sorted[Math.min(k + 1, sorted.length - 1)];
      const span = s1[0] - s0[0];
      const f = span > 0 ? clamp((t - s0[0]) / span, 0, 1) : 0;
      const c0 = s0[1];
      const c1 = s1[1];
      r = ((c0 >> 16) & 255) + ((((c1 >> 16) & 255) - ((c0 >> 16) & 255)) * f);
      gg = ((c0 >> 8) & 255) + ((((c1 >> 8) & 255) - ((c0 >> 8) & 255)) * f);
      b = (c0 & 255) + (((c1 & 255) - (c0 & 255)) * f);
      a = (s0[2] + (s1[2] - s0[2]) * f) * 255;
    } else if (sorted[0][0] > 0) {
      // Fade in from transparent up to the first stop.
      const f = t / sorted[0][0];
      const c0 = sorted[0][1];
      r = (c0 >> 16) & 255;
      gg = (c0 >> 8) & 255;
      b = c0 & 255;
      a = sorted[0][2] * 255 * f * f;
    }
    lut[i * 4] = r;
    lut[i * 4 + 1] = gg;
    lut[i * 4 + 2] = b;
    lut[i * 4 + 3] = a;
  }
  return lut;
}

/** Paint an intensity field through a colour ramp. Pixel centres are sampled at (x + 0.5, y + 0.5). */
export function paintRamp(g: G, x0: number, y0: number, w: number, h: number, field: (x: number, y: number) => number, lut: Uint8ClampedArray): void {
  const img = g.createImageData(w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = field(x + 0.5, y + 0.5);
      if (!(v > 0)) continue;
      const li = Math.min(255, Math.round(v * 255)) * 4;
      const i = (y * w + x) * 4;
      d[i] = lut[li];
      d[i + 1] = lut[li + 1];
      d[i + 2] = lut[li + 2];
      d[i + 3] = lut[li + 3];
    }
  }
  composite(g, img, x0, y0);
}

/** White pixels whose alpha is the field value (tintable FX). */
export function paintAlpha(g: G, x0: number, y0: number, w: number, h: number, field: (x: number, y: number) => number): void {
  const img = g.createImageData(w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const v = field(x + 0.5, y + 0.5);
      if (!(v > 0)) continue;
      const i = (y * w + x) * 4;
      d[i] = 255;
      d[i + 1] = 255;
      d[i + 2] = 255;
      d[i + 3] = Math.min(255, Math.round(v * 255));
    }
  }
  composite(g, img, x0, y0);
}

/** Shared output slot for paintGreyAlpha so field functions allocate nothing. */
export const GA = { grey: 1, alpha: 0 };

/** Greyscale + alpha field (shaded white FX such as the explosion sheet). The field writes into GA. */
export function paintGreyAlpha(g: G, x0: number, y0: number, w: number, h: number, field: (x: number, y: number) => void): void {
  const img = g.createImageData(w, h);
  const d = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      GA.grey = 1;
      GA.alpha = 0;
      field(x + 0.5, y + 0.5);
      if (!(GA.alpha > 0)) continue;
      const i = (y * w + x) * 4;
      const grey = Math.round(clamp(GA.grey, 0, 1) * 255);
      d[i] = grey;
      d[i + 1] = grey;
      d[i + 2] = grey;
      d[i + 3] = Math.round(clamp(GA.alpha, 0, 1) * 255);
    }
  }
  composite(g, img, x0, y0);
}

/** putImageData replaces pixels; draw through a scratch canvas so the field composites over existing art. */
function composite(g: G, img: ImageData, x0: number, y0: number): void {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  c.getContext('2d')!.putImageData(img, 0, 0);
  g.drawImage(c, x0, y0);
}

// ───────────────────────────── Field primitives ─────────────────────────────

export const gauss = (d: number, sigma: number): number => Math.exp(-(d * d) / (2 * sigma * sigma));

/** Smooth 0..1 falloff: 1 at d <= inner, 0 at d >= outer. */
export function falloff(d: number, inner: number, outer: number): number {
  if (d <= inner) return 1;
  if (d >= outer) return 0;
  const t = (d - inner) / (outer - inner);
  return 1 - t * t * (3 - 2 * t);
}

/** Distance from point to segment (x0,y0)-(x1,y1). */
export function segDist(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - x0) * dx + (py - y0) * dy) / len2 : 0;
  t = clamp(t, 0, 1);
  const ex = x0 + dx * t - px;
  const ey = y0 + dy * t - py;
  return Math.sqrt(ex * ex + ey * ey);
}

/**
 * Teardrop / capsule pointing right: round head of radius `r` centred at (hx, cy),
 * tail tapering to a point at tailX. Returns 0..1 (1 on the axis, soft edge `soft` px).
 */
export function teardrop(x: number, y: number, hx: number, cy: number, r: number, tailX: number, soft: number, tailPow = 1): number {
  const dy = Math.abs(y - cy);
  if (x >= hx) {
    const d = Math.hypot(x - hx, dy);
    return falloff(d, r - soft, r + soft * 0.5);
  }
  if (x <= tailX) return 0;
  const u = (x - tailX) / (hx - tailX);
  const hw = r * Math.pow(u, tailPow);
  return falloff(dy, hw - soft, hw + soft * 0.5) * Math.min(1, u * 3);
}
